import { type FormEvent, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import z from "zod";
import { Button } from "@quake/shared-ui/components/ui/button";
import { Input } from "@quake/shared-ui/components/ui/input";
import { Label } from "@quake/shared-ui/components/ui/label";
import { Separator } from "@quake/shared-ui/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@quake/shared-ui/components/ui/select";
import { PlugZapIcon } from "@quake/shared-ui/components/animate-ui/icons/plug-zap";
import { OAuthErrorComponent } from "../-error-component";
import { trpc } from "~/utils/trpc";
import { useMutation, useQuery } from "@tanstack/react-query";

export const Route = createFileRoute("/_oauth/oauth/response")({
  component: RouteComponent,
  validateSearch: (search) => {
    return z
      .object({
        code: z.string(),
        suggestedDatasetName: z.string().optional(),
        datasets: z
          .array(
            z.object({
              id: z.string(),
              name: z.string(),
              slug: z.string(),
            }),
          )
          .optional(),
        partner: z.object({
          id: z.string(),
          logo_url: z.url().optional(),
          name: z.string().optional(),
          website: z.url().optional(),
        }),
        error: z.string().optional(),
        error_description: z.string().optional(),
        is_new_user: z.boolean().optional(),
        user_email: z.email().optional(),
      })
      .parse(search);
  },
  errorComponent: OAuthErrorComponent,
});

function RouteComponent() {
  const { code, datasets, suggestedDatasetName, partner, is_new_user, user_email } = Route.useSearch();
  const datasetOptions = datasets ?? [];
  const primaryDataset = datasetOptions.at(0);
  const requiresUserSignup = Boolean(is_new_user && primaryDataset);

  const [selectedDatasetId, setSelectedDatasetId] = useState<string | undefined>(() => datasetOptions.at(0)?.id);
  const [showCreateForm, setShowCreateForm] = useState(datasetOptions.length === 0);
  const [workspaceName, setWorkspaceName] = useState(() => suggestedDatasetName ?? "");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isSelecting, setIsSelecting] = useState(false);
  const validateCodeQuery = useQuery(
    trpc.partnerOauth.validateCode.queryOptions(
      { code: code, partner: { id: partner?.id } },
      { enabled: !!code && !!partner?.id, retry: false },
    ),
  );
  const linkDatasetMutation = useMutation(trpc.partnerOauth.linkDataset.mutationOptions());
  const createDatasetMutation = useMutation(trpc.partnerOauth.requestNewAccount.mutationOptions());
  const requestUserSignupMutation = useMutation(trpc.partnerOauth.requestUserSignup.mutationOptions());
  const nav = useNavigate();
  const handleSelectWorkspace = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedDatasetId) return;
    setIsSelecting(true);
    console.log("Link workspace", { code, datasetId: selectedDatasetId });
    const { success, redirectPath } = await linkDatasetMutation.mutateAsync({
      dataset_id: selectedDatasetId,
      code: code,
      partner: partner,
    });
    setIsSelecting(false);
    if (success) {
      nav({ to: redirectPath });
    }
  };

  const handleCreateWorkspace = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedName = workspaceName.trim();
    if (!trimmedName || !firstName.trim() || !lastName.trim()) return;
    const result = await createDatasetMutation.mutateAsync({
      name: trimmedName,
      code,
      partner: partner,
      first_name: firstName.trim(),
      last_name: lastName.trim(),
    });
    nav({ to: "/oauth/verify-new-account-request", search: { email: result.email } });
  };

  const handleCompleteProfile = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!primaryDataset || !user_email) return;
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    if (!trimmedFirstName || !trimmedLastName) return;

    await requestUserSignupMutation.mutateAsync({
      dataset_id: primaryDataset.id,
      code,
      partner: partner,
      first_name: trimmedFirstName,
      last_name: trimmedLastName,
      email: user_email,
    });

    nav({
      to: "/oauth/verify-new-user-signup",
      search: {
        email: user_email,
        datasetName: primaryDataset.name,
      },
    });
  };
  if (validateCodeQuery.isLoading) {
    return (
      <div className="space-y-6 py-4 max-w-72 mx-auto">
        <div className="space-y-6">
          <div className="space-y-2 text-center">
            <div className="h-8 bg-muted rounded motion-preset-blink" />
            <div className="h-4 bg-muted rounded motion-preset-blink w-3/4 mx-auto" />
          </div>
          <div className="space-y-2">
            <div className="h-4 bg-muted rounded motion-preset-blink w-1/4" />
            <div className="h-10 bg-muted rounded motion-preset-blink" />
          </div>
          <div className="h-10 bg-muted rounded motion-preset-blink" />
        </div>
      </div>
    );
  }
  if (validateCodeQuery.isError) {
    throw validateCodeQuery.error;
  }

  if (requiresUserSignup && primaryDataset) {
    return (
      <div className="space-y-6 py-4 max-w-72 mx-auto">
        <div className="space-y-2 text-center">
          <h1 className="text-2xl font-semibold">Join {partner?.name ?? "Quake"}</h1>
          <p className="text-sm text-muted-foreground">
            Please help us complete your profile before joining {primaryDataset.name}.
          </p>
        </div>

        <form className="space-y-4" onSubmit={handleCompleteProfile}>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2 text-left">
              <Label htmlFor="profile-first-name">First Name</Label>
              <Input
                id="profile-first-name"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                placeholder="First name"
              />
            </div>
            <div className="space-y-2 text-left">
              <Label htmlFor="profile-last-name">Last Name</Label>
              <Input
                id="profile-last-name"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                placeholder="Last name"
              />
            </div>
          </div>

          <div className="space-y-2 text-left">
            <Label htmlFor="profile-email">Email</Label>
            <Input id="profile-email" value={user_email ?? ""} disabled readOnly />
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={!firstName.trim() || !lastName.trim() || !user_email || requestUserSignupMutation.isPending}
          >
            <PlugZapIcon size={18} className="mr-2" />
            {requestUserSignupMutation.isPending ? "Submitting..." : `Continue to ${primaryDataset.name}`}
          </Button>
        </form>

        <p className="text-center text-xs text-muted-foreground">
          We'll email a verification code to {user_email} to finish joining {primaryDataset.name}.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 py-4 max-w-72 mx-auto">
      {!showCreateForm && datasetOptions.length > 0 ? (
        <div className="space-y-6">
          <div className="space-y-2 text-center">
            <h1 className="text-2xl font-semibold">Link {partner?.name ?? ""}</h1>
            <p className="text-sm text-muted-foreground">
              Choose an existing Quake workspace to complete the connection.
            </p>
          </div>

          <form className="space-y-4" onSubmit={handleSelectWorkspace}>
            <div className="space-y-2 text-left">
              <Label htmlFor="workspace-select">Quake Workspace</Label>
              <Select value={selectedDatasetId} onValueChange={setSelectedDatasetId}>
                <SelectTrigger id="workspace-select" className="w-full">
                  <SelectValue placeholder="Select a workspace" />
                </SelectTrigger>
                <SelectContent>
                  {datasetOptions.map((dataset) => (
                    <SelectItem key={dataset.id} value={dataset.id}>
                      {dataset.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button type="submit" className="w-full" disabled={!selectedDatasetId || isSelecting}>
              {isSelecting ? "Linking..." : "Link Workspace..."}
            </Button>
          </form>

          <div className="relative flex items-center">
            <Separator className="w-full" />
            <span className="absolute inset-0 flex items-center justify-center px-2 text-xs text-muted-foreground">
              or
            </span>
          </div>

          <Button type="button" variant="outline" className="w-full" onClick={() => setShowCreateForm(true)}>
            Create a new Quake Account Instead
          </Button>
        </div>
      ) : null}

      {(showCreateForm || datasetOptions.length === 0) && (
        <div className="space-y-6">
          <div className="space-y-2 text-center">
            <h1 className="text-2xl font-semibold">Create your Quake Workspace</h1>
            <p className="text-sm text-muted-foreground">
              Set up a workspace to finish connecting {partner?.name ?? ""}.
            </p>
          </div>

          <form className="space-y-4" onSubmit={handleCreateWorkspace}>
            <div className="space-y-2 text-left">
              <Label htmlFor="workspace-name">Workspace Name</Label>
              <Input
                id="workspace-name"
                value={workspaceName}
                onChange={(event) => setWorkspaceName(event.target.value)}
                placeholder="Enter workspace name"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2 text-left">
                <Label htmlFor="first-name">First Name</Label>
                <Input
                  id="first-name"
                  value={firstName}
                  onChange={(event) => setFirstName(event.target.value)}
                  placeholder="First name"
                />
              </div>
              <div className="space-y-2 text-left">
                <Label htmlFor="last-name">Last Name</Label>
                <Input
                  id="last-name"
                  value={lastName}
                  onChange={(event) => setLastName(event.target.value)}
                  placeholder="Last name"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={
                !workspaceName.trim() || !firstName.trim() || !lastName.trim() || createDatasetMutation.isPending
              }
            >
              <PlugZapIcon size={18} className="mr-2" />
              {createDatasetMutation.isPending ? "Requesting..." : "Create Quake Workspace"}
            </Button>
          </form>

          {datasetOptions.length > 0 && showCreateForm ? (
            <Button type="button" variant="ghost" className="w-full" onClick={() => setShowCreateForm(false)}>
              Select an existing workspace instead
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
}
